import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import _ from 'lodash';

/**
 * Resolves a stored state or province code into the label Biotricity displays. Looks the code up in the US or CA state list according to the country and returns its label, passes the raw state through for every other country, and yields an empty string when no state is set.
 *
 * @param state - State or province code stored on the address.
 * @param country - Two-letter country code of the address.
 * @param listStatesUS - US state options to resolve the code against.
 * @param listStatesCA - Canadian province options to resolve the code against.
 * @returns State label, the raw state code, or an empty string.
 */
const formatStateBiotricity = (state: any, country: any, listStatesUS: any, listStatesCA: any) => {
  if (state) {
    if (country === 'US' || country === 'CA') {
      if (country === 'US') {
        const data = _.find(listStatesUS, ['value', state]);
        return data?.label;
      }
      if (country === 'CA') {
        const data = _.find(listStatesCA, ['value', state]);
        return data?.label;
      }
    }
    return state;
  }
  return '';
};

/**
 * Renders an address record as the single comma-separated line shown in drawers and tables. Appends street1, street2, city, the resolved state label and zip in order while skipping empty parts, and returns the placeholder character when nothing was collected.
 *
 * @param data - Address fields: street1, street2, city, state, zip and country.
 * @param listStatesUS - US state options used to resolve the state code to a label.
 * @param listStatesCA - Canadian province options used to resolve the state code to a label.
 * @returns Comma-joined address line, or the placeholder character.
 */
const generateAddress = ({ data, listStatesUS, listStatesCA }: any) => {
  const { street1, street2, city, state, zip, country } = data || {};
  const result = [];
  if (street1) {
    result.push(street1);
  }
  if (street2) {
    result.push(street2);
  }
  if (city) {
    result.push(city);
  }
  const formattedState = formatStateBiotricity(state, country, listStatesUS, listStatesCA);
  if (formattedState) {
    result.push(formattedState);
  }
  if (zip) {
    result.push(zip);
  }

  if (result.length === 0) {
    return DOUBLE_HYPHEN_BULLET;
  }
  return result.join(', ');
};

export { formatStateBiotricity, generateAddress };
